import {
  Alert,
  Backdrop,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Icon,
  InputAdornment,
  MenuItem,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";

import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios.js";
import ExcelJS from "exceljs";

import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";

import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDButton from "theme/components/MDButton";

import usePageTitle from "app/pageTitle";
import { useAwaitDialog } from "app/components/status_dialog";

function WhatsAppBroadcastViewPage() {
  const pageTitle = "WhatsApp - Broadcast";
  usePageTitle(pageTitle);

  const navigate = useNavigate();
  const { ref } = useParams();

  const statusDialog = useAwaitDialog();

  const [showLoading, setShowLoading] = useState(false);
  const [pageLoaded, setPageLoaded] = useState(false);

  const [broadcast, setBroadcast] = useState(null);
  const [stats, setStats] = useState({});
  const [contacts, setContacts] = useState([]);
  const [contactsPage, setContactsPage] = useState(1);
  const [contactsPageSize, setContactsPageSize] = useState(25);
  const [contactsTotal, setContactsTotal] = useState(0);
  const [contactsPages, setContactsPages] = useState(0);

  const [phoneNumber, setPhoneNumber] = useState("");
  const [sent, setSent] = useState("");
  const [delivered, setDelivered] = useState("");
  const [read, setRead] = useState("");
  const [replied, setReplied] = useState("");

  const [open, setOpen] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [severity, setSeverity] = useState("error");

  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);

  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduledDate, setScheduledDate] = useState("");
  const [scheduledTime, setScheduledTime] = useState("");

  const [cancelOpen, setCancelOpen] = useState(false);

  const handleClose = () => {
    setOpen(false);
    setErrorMsg("");
  };

  const fetchData = async () => {
    setShowLoading(true);

    const params = new URLSearchParams();

    if (phoneNumber) {
      params.append("phone_number", phoneNumber);
    }

    if (sent !== "") {
      params.append("sent", sent);
    }

    if (delivered !== "") {
      params.append("delivered", delivered);
    }

    if (read !== "") {
      params.append("read", read);
    }

    if (replied !== "") {
      params.append("replied", replied);
    }

    params.append("page", contactsPage);
    params.append("page_size", contactsPageSize);

    const query = params.toString();

    await axios
      .get(`/portal/wacc/broadcasts/${ref}/fetch.json${query ? `?${query}` : ""}`)
      .then((result) => {
        setShowLoading(false);

        if (result.data.status === "success") {
          setBroadcast(result.data.data.broadcast || null);
          setStats(result.data.data.stats || {});
          setContacts(result.data.data.contacts || []);
          setContactsTotal(result.data.data.contacts_total || 0);
          setContactsPage(result.data.data.contacts_page || 1);
          setContactsPageSize(result.data.data.contacts_page_size || 25);
          setContactsPages(result.data.data.contacts_pages || 0);
          setPageLoaded(true);
        } else {
          setErrorMsg(result.data.data.message);
          setSeverity("error");
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);

        setErrorMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );

        setSeverity("error");
        setOpen(true);
      });
  };

  useEffect(() => {
    fetchData();
  }, [ref, phoneNumber, sent, delivered, read, contactsPage, contactsPageSize]);

  const getAttributeColumns = () => {
    const columns = [];

    contacts.forEach((contact) => {
      if (contact.attributes && typeof contact.attributes === "object") {
        Object.keys(contact.attributes).forEach((key) => {
          if (!columns.includes(key)) {
            columns.push(key);
          }
        });
      }
    });

    return columns;
  };

  const attributeColumns = useMemo(() => getAttributeColumns(), [contacts]);

  const hasScheduledTime =
    broadcast && broadcast.scheduled_time && broadcast.scheduled_time !== "--";

  const extractTemplateVariables = (components) => {
    const variables = [];

    const extract = (value) => {
      if (Array.isArray(value)) {
        value.forEach((item) => extract(item));
        return;
      }

      if (value && typeof value === "object") {
        Object.values(value).forEach((item) => extract(item));
        return;
      }

      if (typeof value === "string") {
        const matches = value.match(/\{\{\s*([^{}]+?)\s*\}\}/g);

        if (matches) {
          matches.forEach((match) => {
            const variable = match.replace("{{", "").replace("}}", "").trim();

            if (variable && !variables.includes(variable)) {
              variables.push(variable);
            }
          });
        }
      }
    };

    extract(components);

    return variables;
  };

  const handleDownloadSample = async () => {
    const components = broadcast?.template?.components || [];
    const variables = extractTemplateVariables(components);
    const columns = ["phone_number", ...variables];

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Contacts");

    worksheet.columns = columns.map((column) => ({
      header: column,
      key: column,
      width: column === "phone_number" ? 20 : 25,
    }));

    const sampleRow = {
      phone_number: "2348012345678",
    };

    variables.forEach((variable) => {
      sampleRow[variable] = `Sample ${variable}`;
    });

    worksheet.addRow(sampleRow);

    worksheet.getRow(1).font = {
      bold: true,
    };

    worksheet.freezePanes = {
      ySplit: 1,
    };

    const buffer = await workbook.xlsx.writeBuffer();

    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = `${broadcast?.title || "broadcast"}_contacts_sample.xlsx`;
    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleUpload = async () => {
    if (!uploadFile) {
      setErrorMsg("Select a CSV or Excel file");
      setSeverity("error");
      setOpen(true);
      return;
    }

    const formData = new FormData();
    formData.append("file", uploadFile);

    setShowLoading(true);
    setUploadOpen(false);

    await axios
      .post(`/portal/wacc/broadcasts/${ref}/contacts/upload.json`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then((result) => {
        setShowLoading(false);

        if (result.data.status === "success") {
          setUploadFile(null);
          setContactsPage(1);

          fetchData();
        } else {
          setErrorMsg(result.data.data.message);
          setSeverity("error");
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);

        setErrorMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );

        setSeverity("error");
        setOpen(true);
      });
  };

  const handleSchedule = async () => {
    if (!scheduledDate || !scheduledTime) {
      setErrorMsg("Select a date and time");
      setSeverity("error");
      setOpen(true);
      return;
    }

    const scheduledTimeValue = `${scheduledDate}T${scheduledTime}:00`;

    setShowLoading(true);
    setScheduleOpen(false);

    await axios
      .post(`/portal/wacc/broadcasts/${ref}/schedule.json`, {
        scheduled_time: scheduledTimeValue,
      })
      .then((result) => {
        setShowLoading(false);

        if (result.data.status === "success") {
          setScheduleOpen(false);
          setScheduledDate("");
          setScheduledTime("");

          fetchData();
        } else {
          setErrorMsg(result.data.data.message);
          setSeverity("error");
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);

        setErrorMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );

        setSeverity("error");
        setOpen(true);
      });
  };

  const handleCancel = async () => {
    setCancelOpen(false);
    setShowLoading(true);

    await axios
      .post(`/portal/wacc/broadcasts/${ref}/cancel.json`)
      .then((result) => {
        setShowLoading(false);

        if (result.data.status === "success") {
          fetchData();
        } else {
          setErrorMsg(result.data.data.message);
          setSeverity("error");
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);

        setErrorMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );

        setSeverity("error");
        setOpen(true);
      });
  };

  const handleExport = async () => {
    const params = new URLSearchParams();

    if (phoneNumber) {
      params.append("phone_number", phoneNumber);
    }

    if (sent !== "") {
      params.append("sent", sent);
    }

    if (delivered !== "") {
      params.append("delivered", delivered);
    }

    if (read !== "") {
      params.append("read", read);
    }

    if (replied !== "") {
      params.append("replied", replied);
    }

    const query = params.toString();

    setShowLoading(true);

    await axios
      .get(`/portal/wacc/broadcasts/contacts/${ref}/export.json${query ? `?${query}` : ""}`, {
        responseType: "blob",
      })
      .then((result) => {
        setShowLoading(false);

        const blob = new Blob([result.data], {
          type: "text/csv",
        });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "broadcast_contacts.csv";
        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch(async (error) => {
        setShowLoading(false);

        let message = "Unable to export contacts.";

        if (error.response?.data instanceof Blob) {
          try {
            const text = await error.response.data.text();

            const data = JSON.parse(text);

            if (data?.data?.message) {
              message = data.data.message;
            }
          } catch (e) {
            // CSV/file response could not be parsed as JSON
          }
        }

        setErrorMsg(message);
        setSeverity("error");
        setOpen(true);
      });
  };

  const clearFilters = () => {
    setPhoneNumber("");
    setSent("");
    setDelivered("");
    setRead("");
    setContactsPage(1);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
        open={showLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>

      {/* UPLOAD CONTACTS */}
      <Dialog open={uploadOpen} onClose={() => setUploadOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Upload Contacts</DialogTitle>

        <DialogContent>
          <MDBox pt={1}>
            <MDTypography
              sx={{
                fontSize: "13px",
                mb: 2,
              }}
            >
              Upload a CSV or Excel file containing
              <strong> phone_number</strong> and the variables required by the selected template.
            </MDTypography>

            <MDButton
              variant="outlined"
              color="dark"
              size="small"
              startIcon={<Icon>download</Icon>}
              onClick={handleDownloadSample}
              sx={{
                mb: 2,
              }}
            >
              Download Sample Excel
            </MDButton>

            <MDButton
              component="label"
              variant="outlined"
              color="dark"
              fullWidth
              sx={{
                py: 1.5,
              }}
            >
              <Icon
                sx={{
                  mr: 1,
                }}
              >
                upload_file
              </Icon>

              {uploadFile ? uploadFile.name : "Select CSV or Excel file"}

              <input
                type="file"
                hidden
                accept=".csv,.xlsx,.xls"
                onChange={(event) => setUploadFile(event.target.files?.[0] || null)}
              />
            </MDButton>
          </MDBox>
        </DialogContent>

        <DialogActions>
          <Button color="inherit" onClick={() => setUploadOpen(false)}>
            Cancel
          </Button>

          <MDButton color="dark" variant="contained" onClick={handleUpload}>
            Upload
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* SCHEDULE */}
      <Dialog open={scheduleOpen} onClose={() => setScheduleOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Schedule Broadcast</DialogTitle>

        <DialogContent>
          <MDBox pt={1}>
            <MDTypography
              sx={{
                fontSize: "13px",
                mb: 2,
              }}
            >
              Select when the broadcast should be sent. Broadcasts can only be scheduled on the
              hour.
            </MDTypography>

            <Grid container spacing={2}>
              <Grid item xs={7}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Date"
                  value={scheduledDate}
                  onChange={(event) => setScheduledDate(event.target.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>

              <Grid item xs={5}>
                <TextField
                  fullWidth
                  size="small"
                  type="time"
                  label="Time"
                  value={scheduledTime}
                  onChange={(event) => {
                    const value = event.target.value;

                    if (value.endsWith(":00")) {
                      setScheduledTime(value);
                    }
                  }}
                  inputProps={{
                    step: 3600,
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
            </Grid>
          </MDBox>
        </DialogContent>

        <DialogActions>
          <Button color="inherit" onClick={() => setScheduleOpen(false)}>
            Cancel
          </Button>

          <MDButton color="dark" variant="contained" onClick={handleSchedule}>
            Schedule
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* CANCEL */}
      <Dialog open={cancelOpen} onClose={() => setCancelOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Cancel Broadcast</DialogTitle>

        <DialogContent>
          <MDTypography
            sx={{
              fontSize: "13px",
            }}
          >
            Are you sure you want to cancel this scheduled broadcast?
          </MDTypography>
        </DialogContent>

        <DialogActions>
          <Button color="inherit" onClick={() => setCancelOpen(false)}>
            Keep Broadcast
          </Button>

          <MDButton color="dark" variant="contained" onClick={handleCancel}>
            Cancel Broadcast
          </MDButton>
        </DialogActions>
      </Dialog>

      {pageLoaded && broadcast && (
        <MDBox mb={5}>
          <MDBox mb={3}>

            <MDBox mt={1} display="flex" justifyContent="space-between" alignItems="center">
              <MDBox pl={1}>
                <MDTypography variant="caption">Broadcast</MDTypography>
                <MDTypography variant="h5">{broadcast.title}</MDTypography>
              </MDBox>

              <MDBox display="flex" gap={1}>
                <MDButton
                  variant="outlined"
                  color="dark"
                  size="small"
                  startIcon={<Icon>west</Icon>}
                  onClick={() => navigate(-1)}
                >
                  Back
                </MDButton>
                <MDButton
                  variant="outlined"
                  color="dark"
                  size="small"
                  startIcon={<Icon>upload</Icon>}
                  onClick={() => setUploadOpen(true)}
                  disabled={broadcast.status === "COMPLETED" ? true : false}
                >
                  Upload Contacts
                </MDButton>

                {!hasScheduledTime ? (
                  <MDButton
                    variant="contained"
                    color="dark"
                    size="small"
                    startIcon={<Icon>schedule</Icon>}
                    onClick={() => setScheduleOpen(true)}
                    disabled={broadcast.status === "COMPLETED" ? true : false}
                  >
                    Schedule
                  </MDButton>
                ) : (
                  <MDButton
                    variant="outlined"
                    color="dark"
                    size="small"
                    startIcon={<Icon>cancel</Icon>}
                    onClick={() => setCancelOpen(true)}
                    disabled={broadcast.status === "COMPLETED" ? true : false}
                  >
                    Cancel
                  </MDButton>
                )}
              </MDBox>
            </MDBox>
          </MDBox>

          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
              <Card sx={{ height: "100%", width: "100%" }}>
                <MDBox p={2}>
                  <MDTypography
                    sx={{
                      fontSize: "11px",
                      color: "text.secondary",
                      mb: 0.5,
                    }}
                  >
                    TEMPLATE
                  </MDTypography>

                  <MDTypography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 600,
                    }}
                  >
                    {broadcast.template?.title || "--"}
                  </MDTypography>
                </MDBox>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
              <Card sx={{ height: "100%", width: "100%" }}>
                <MDBox p={2}>
                  <MDTypography
                    sx={{
                      fontSize: "11px",
                      color: "text.secondary",
                      mb: 0.5,
                    }}
                  >
                    BUSINESS NUMBER
                  </MDTypography>

                  <MDTypography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 600,
                    }}
                  >
                    {broadcast.business_number?.phone_number || "--"}
                  </MDTypography>
                </MDBox>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
              <Card sx={{ height: "100%", width: "100%" }}>
                <MDBox p={2}>
                  <MDTypography
                    sx={{
                      fontSize: "11px",
                      color: "text.secondary",
                      mb: 0.5,
                    }}
                  >
                    SCHEDULED ON
                  </MDTypography>

                  <MDTypography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 600,
                    }}
                  >
                    {broadcast.scheduled_time || "--"}
                  </MDTypography>
                </MDBox>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
              <Card sx={{ height: "100%", width: "100%" }}>
                <MDBox p={2}>
                  <MDTypography
                    sx={{
                      fontSize: "11px",
                      color: "text.secondary",
                      mb: 0.5,
                    }}
                  >
                    STATUS
                  </MDTypography>

                  <MDTypography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 600,
                      textTransform: "capitalize",
                    }}
                  >
                    {broadcast.status || "--"}
                  </MDTypography>
                </MDBox>
              </Card>
            </Grid>
          </Grid>

          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card>
                <MDBox p={2} display="flex" justifyContent="space-between" alignItems="top">
                  <MDBox>
                    <MDTypography
                      sx={{
                        fontSize: "11px",
                        color: "text.secondary",
                      }}
                    >
                      CONTACTS
                    </MDTypography>

                    <MDTypography
                      sx={{
                        fontSize: "24px",
                        fontWeight: 600,
                        mt: 0.5,
                      }}
                    >
                      {stats.total_contacts || 0}
                    </MDTypography>
                  </MDBox>

                  <Icon
                    sx={{
                      fontSize: 28,
                      color: "text.secondary",
                    }}
                  >
                    contacts
                  </Icon>
                </MDBox>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={2.4}>
              <Card>
                <MDBox p={2} display="flex" justifyContent="space-between" alignItems="top">
                  <MDBox>
                    <MDTypography
                      sx={{
                        fontSize: "11px",
                        color: "text.secondary",
                      }}
                    >
                      SENT
                    </MDTypography>

                    <MDTypography
                      sx={{
                        fontSize: "24px",
                        fontWeight: 600,
                        mt: 0.5,
                      }}
                    >
                      {stats.sent || 0}
                    </MDTypography>
                  </MDBox>

                  <Icon
                    sx={{
                      fontSize: 28,
                      color: "text.secondary",
                    }}
                  >
                    send
                  </Icon>
                </MDBox>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={2.4}>
              <Card>
                <MDBox p={2} display="flex" justifyContent="space-between" alignItems="top">
                  <MDBox>
                    <MDTypography
                      sx={{
                        fontSize: "11px",
                        color: "text.secondary",
                      }}
                    >
                      DELIVERED
                    </MDTypography>

                    <MDTypography
                      sx={{
                        fontSize: "24px",
                        fontWeight: 600,
                        mt: 0.5,
                      }}
                    >
                      {stats.delivered || 0}
                    </MDTypography>
                  </MDBox>

                  <Icon
                    sx={{
                      fontSize: 28,
                      color: "text.secondary",
                    }}
                  >
                    done_all
                  </Icon>
                </MDBox>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={2.4}>
              <Card>
                <MDBox p={2} display="flex" justifyContent="space-between" alignItems="top">
                  <MDBox>
                    <MDTypography
                      sx={{
                        fontSize: "11px",
                        color: "text.secondary",
                      }}
                    >
                      READ
                    </MDTypography>

                    <MDTypography
                      sx={{
                        fontSize: "24px",
                        fontWeight: 600,
                        mt: 0.5,
                      }}
                    >
                      {stats.read || 0}
                    </MDTypography>
                  </MDBox>

                  <Icon
                    sx={{
                      fontSize: 28,
                      color: "text.secondary",
                    }}
                  >
                    mark_email_read
                  </Icon>
                </MDBox>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card>
                <MDBox p={2} display="flex" justifyContent="space-between" alignItems="top">
                  <MDBox>
                    <MDTypography
                      sx={{
                        fontSize: "11px",
                        color: "text.secondary",
                      }}
                    >
                      REPLIED
                    </MDTypography>

                    <MDTypography
                      sx={{
                        fontSize: "24px",
                        fontWeight: 600,
                        mt: 0.5,
                      }}
                    >
                      {stats.replied || 0}
                    </MDTypography>
                  </MDBox>

                  <Icon
                    sx={{
                      fontSize: 28,
                      color: "text.secondary",
                    }}
                  >
                    reply
                  </Icon>
                </MDBox>
              </Card>
            </Grid>
          </Grid>

          <MDBox mt={5}>
            <Card>
              <MDBox p={2}>
                <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <MDBox>
                    <MDTypography variant="h6">Contacts</MDTypography>

                    <MDTypography
                      sx={{
                        fontSize: "11px",
                        color: "text.secondary",
                      }}
                    >
                      {contactsTotal} contact
                      {contactsTotal === 1 ? "" : "s"} found
                    </MDTypography>
                  </MDBox>

                  <MDButton
                    variant="outlined"
                    color="dark"
                    size="small"
                    startIcon={<Icon>download</Icon>}
                    onClick={handleExport}
                  >
                    Export
                  </MDButton>
                </MDBox>

                {/* FILTERS */}
                <MDBox display="flex" gap={1} flexWrap="wrap" mb={2}>
                  <TextField
                    placeholder="Phone number"
                    value={phoneNumber}
                    onChange={(event) => {
                      setPhoneNumber(event.target.value);
                      setContactsPage(1);
                    }}
                    sx={{
                      width: 220,
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Icon
                            sx={{
                              fontSize: 18,
                              color: "text.secondary",
                            }}
                          >
                            search
                          </Icon>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Select
                    displayEmpty
                    value={sent}
                    onChange={(event) => {
                      setSent(event.target.value);
                      setContactsPage(1);
                    }}
                    sx={{
                      minWidth: 130,
                    }}
                  >
                    <MenuItem value="">Sent: All</MenuItem>
                    <MenuItem value="true">Sent: Yes</MenuItem>
                    <MenuItem value="false">Sent: No</MenuItem>
                  </Select>

                  <Select
                    displayEmpty
                    value={delivered}
                    onChange={(event) => {
                      setDelivered(event.target.value);
                      setContactsPage(1);
                    }}
                    sx={{
                      minWidth: 150,
                    }}
                  >
                    <MenuItem value="">Delivered: All</MenuItem>
                    <MenuItem value="true">Delivered: Yes</MenuItem>
                    <MenuItem value="false">Delivered: No</MenuItem>
                  </Select>

                  <Select
                    size="small"
                    displayEmpty
                    value={read}
                    onChange={(event) => {
                      setRead(event.target.value);
                      setContactsPage(1);
                    }}
                    sx={{
                      minWidth: 130,
                    }}
                  >
                    <MenuItem value="">Read: All</MenuItem>
                    <MenuItem value="true">Read: Yes</MenuItem>
                    <MenuItem value="false">Read: No</MenuItem>
                  </Select>
                  <Select
                    size="small"
                    displayEmpty
                    value={read}
                    onChange={(event) => {
                      setReplied(event.target.value);
                      setContactsPage(1);
                    }}
                    sx={{
                      minWidth: 130,
                    }}
                  >
                    <MenuItem value="">Replied: All</MenuItem>
                    <MenuItem value="true">Replied: Yes</MenuItem>
                    <MenuItem value="false">Replied: No</MenuItem>
                  </Select>

                  {(phoneNumber || sent || delivered || read || replied) && (
                    <Button size="small" color="inherit" onClick={clearFilters}>
                      Clear
                    </Button>
                  )}
                </MDBox>

                <Divider sx={{ mb: 1 }} />

                <MDBox
                  sx={{
                    width: "100%",
                    overflowX: "auto",
                  }}
                >
                  <Table
                    sx={{
                      width: "100%",
                      tableLayout: "auto",
                    }}
                  >
                    <TableRow>
                      <TableCell
                        sx={{
                          fontSize: "11px",
                          fontWeight: 700,
                        }}
                      >
                        Phone Number
                      </TableCell>

                      {attributeColumns.map((column) => (
                        <TableCell
                          key={column}
                          sx={{
                            fontSize: "11px",
                            fontWeight: 700,
                          }}
                        >
                          {column}
                        </TableCell>
                      ))}

                      <TableCell
                        sx={{
                          fontSize: "11px",
                          fontWeight: 700,
                        }}
                      >
                        Sent
                      </TableCell>

                      <TableCell
                        sx={{
                          fontSize: "11px",
                          fontWeight: 700,
                        }}
                      >
                        Delivered
                      </TableCell>

                      <TableCell
                        sx={{
                          fontSize: "11px",
                          fontWeight: 700,
                        }}
                      >
                        Read
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: "11px",
                          fontWeight: 700,
                        }}
                      >
                        Replied
                      </TableCell>
                    </TableRow>

                    <TableBody>
                      {contacts.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4 + attributeColumns.length} align="center">
                            <MDBox py={5}>
                              <MDTypography
                                sx={{
                                  fontSize: "13px",
                                  color: "text.secondary",
                                }}
                              >
                                No contacts found
                              </MDTypography>
                            </MDBox>
                          </TableCell>
                        </TableRow>
                      ) : (
                        contacts.map((contact) => (
                          <TableRow key={contact.reference} hover>
                            <TableCell
                              sx={{
                                fontSize: "12px",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {contact.phone_number}
                            </TableCell>

                            {attributeColumns.map((column) => (
                              <TableCell
                                key={column}
                                sx={{
                                  fontSize: "12px",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {contact.attributes?.[column] || ""}
                              </TableCell>
                            ))}

                            <TableCell
                              sx={{
                                fontSize: "12px",
                              }}
                            >
                              {contact.sent ? "Yes" : "No"}
                            </TableCell>

                            <TableCell
                              sx={{
                                fontSize: "12px",
                              }}
                            >
                              {contact.delivered ? "Yes" : "No"}
                            </TableCell>

                            <TableCell
                              sx={{
                                fontSize: "12px",
                              }}
                            >
                              {contact.read ? "Yes" : "No"}
                            </TableCell>
                            <TableCell
                              sx={{
                                fontSize: "12px",
                              }}
                            >
                              {contact.replied ? "Yes" : "No"}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </MDBox>

                <MDBox mt={2} display="flex" justifyContent="space-between" alignItems="center">
                  <MDTypography
                    sx={{
                      fontSize: "13px",
                      color: "text.secondary",
                    }}
                  >
                    {contactsTotal > 0
                      ? `${(contactsPage - 1) * contactsPageSize + 1}-${Math.min(
                          contactsPage * contactsPageSize,
                          contactsTotal
                        )} of ${contactsTotal}`
                      : "0 contacts"}
                  </MDTypography>

                  <MDBox display="flex" alignItems="center" gap={1}>
                    <Select
                      size="small"
                      value={contactsPageSize}
                      onChange={(event) => {
                        setContactsPageSize(event.target.value);
                        setContactsPage(1);
                      }}
                      sx={{
                        minWidth: 75,
                        fontSize: "13px",
                      }}
                    >
                      <MenuItem value={25}>25</MenuItem>
                      <MenuItem value={50}>50</MenuItem>
                      <MenuItem value={100}>100</MenuItem>
                    </Select>

                    <Button
                      size="small"
                      color="dark"
                      disabled={contactsPage <= 1}
                      onClick={() => setContactsPage((current) => current - 1)}
                      sx={{
                        minWidth: 32,
                        px: 0.5,
                      }}
                    >
                      <Icon>chevron_left</Icon>
                    </Button>

                    <MDTypography
                      sx={{
                        fontSize: "13px",
                        minWidth: 60,
                        textAlign: "center",
                      }}
                    >
                      {contactsPages > 0 ? `${contactsPage} / ${contactsPages}` : "0 / 0"}
                    </MDTypography>

                    <Button
                      size="small"
                      color="dark"
                      disabled={contactsPages === 0 || contactsPage >= contactsPages}
                      onClick={() => setContactsPage((current) => current + 1)}
                      sx={{
                        minWidth: 32,
                        px: 0.5,
                      }}
                    >
                      <Icon>chevron_right</Icon>
                    </Button>
                  </MDBox>
                </MDBox>
              </MDBox>
            </Card>
          </MDBox>
        </MDBox>
      )}

      <Footer />
    </DashboardLayout>
  );
}

export default WhatsAppBroadcastViewPage;
